import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "./ServicePages.css";
import tax from "./assets/tax-man.png";
import hero from "./assets/LocalSEO/local-seo-services-for-accountants-hero.png";
import value from "./assets/LocalSEO/google-business-profile-accountant-before-after.png";
import process from "./assets/LocalSEO/accountant-seo-keyword-examples.png";
import sixStep from "./assets/LocalSEO/accountant-seo-six-step-checklist.png";
import phone from "./assets/LocalSEO/attoli-free-local-seo-call.png";
import Footer from "./Footer";

const LocalSEO = () => {
  return (
    <div className="page-wrapper">
      <Helmet>
        <meta
          name="description"
          content="Attoli helps solo accountants and small firms show up first when local clients search on Google. Book a free call to see where you rank today."
        />
        <title>Local SEO Services for Accountants</title>
        <link
          rel="canonical"
          href="https://attoli.com/services/local-seo-services-for-accountants"
        />
      </Helmet>

      <div className="servicePage-container">
        <section className="servicePage-section hero-section">
          <div className="servicePage-content">
            <div className="servicePage-left">
              <h1 className="servicePage-title">
                Local SEO Services for{" "}
                <span className="hero-accent">Accountants</span>
              </h1>
              <p className="servicePage-description">
                Show up first when someone near your office searches for an
                accountant. Most firms start seeing new local traffic within 90
                days and steady lead flow after that.
              </p>

              <ul className="trust-badges">
                <li>No lock in contracts</li>
                <li>Built specifically for accounting and bookkeeping firms</li>
                <li>Free site audit before you commit to anything</li>
              </ul>

              <div className="cta-block">
                <Link to="/contact" className="cta-button">
                  Book A Free Audit Today
                </Link>
              </div>
            </div>

            <div className="servicePage-right">
              <img
                src={hero}
                alt="Phone showing a Google search for accountant near me with a small accounting office in the background"
                className="servicePage-image"
              />
            </div>
          </div>
        </section>

        <section className="servicePage-section pain-section">
          <div className="section-inner">
            <h2 className="section-title">
              The Real Cost Of Skipping Accountants SEO
            </h2>
            <p className="body-copy">
              Somewhere near your office right now a business owner is searching
              for an accountant. Google shows three or four firms at the top of
              the map. If your firm is not one of them that owner may never sees
              you. They call whoever shows up first.
            </p>
            <p className="body-copy">
              This happens every single day. Local search traffic keeps growing
              and most accountants ignore it. Your competitors do not need to be
              better than you. You only need to be easier to find.
            </p>
            <p className="body-copy">
              A thin or outdated Google Business Profile pushes you further down
              the map. Missing reviews can send a signal to Google and to buyers
              that your firm is inactive. Inconsistent business information
              across the web confuses search engines and costs you top of page
              rankings you should already own.
            </p>
            <p className="body-copy body-copy-emphasis">
              None of this fixes itself. It takes a focused plan built around
              how local search actually works.
            </p>
          </div>
        </section>

        <section className="servicePage-section value-section">
          <div className="section-inner">
            <h2 className="section-title">
              What A Local SEO Company For Accountants Actually Does
            </h2>
            <p className="body-copy">
              Attoli builds and manages the local SEO work your firm does not
              have time for.
            </p>

            <div className="value-grid">
              <div className="value-card">
                <h3>Google Business Profile Optimization</h3>
                <p>
                  We fill out and manage your Google Business Profile
                  completely. A complete profile with photos, hours, services,
                  and monthly posting ranks higher and earns more calls than a
                  half finished one.
                </p>
              </div>
              <div className="value-card">
                <h3>Local Keyword Research</h3>
                <p>
                  We find the exact phrases local clients type before hiring an
                  accountant. Your site gets built around what people actually
                  search for in your city.
                </p>
              </div>
              <div className="value-card">
                <h3>Local Landing Page Creation</h3>
                <p>
                  We build pages that target your services and your service
                  area. These pages give Google a clear reason to rank your firm
                  for local searches.
                </p>
              </div>
              <div className="value-card">
                <h3>Review Generation Support</h3>
                <p>
                  We help you build a steady flow of new reviews from real
                  clients. Recent reviews push your profile above firms that
                  stopped collecting them.
                </p>
              </div>
              <div className="value-card">
                <h3>Monthly Tracking And Reporting</h3>
                <p>
                  We track your rankings calls and website visits every month.
                  You always know exactly what your local SEO investment is
                  doing for your firm.
                </p>
              </div>
            </div>
            <img
              src={value}
              alt="A mostly empty Google Business Profile next to a fully filled out one with photos hours and services listed"
              className="inline-image"
            />
          </div>
        </section>

        <section className="servicePage-section keyword-section">
          <div className="section-inner">
            <h2 className="section-title">
              The Best Local SEO Tools We Use For Your Firm
            </h2>
            <p className="body-copy">
              Every accounting firm searches differently depending on the city
              and the service. We research your specific market instead of
              guessing.
            </p>
            <img
              src={process}
              alt="Sticky notes with example local keyword phrases for accountants"
              className="inline-image"
            />

            <p className="body-copy">
              We combine tools like Google Search Console, Google Business
              Profile Insights, Ahrefs, and Google Ads Keyword Exporer to find
              where your firm can win fastest. The goal is never a long list of
              keywords. It is a short focused list built around real searches
              from real clients in your area.
            </p>
          </div>
        </section>

        <section className="servicePage-section process-section">
          <div className="section-inner">
            <h2 className="section-title">
              How Our SEO Accounting Process Works Step By Step
            </h2>

            <img
              src={sixStep}
              alt="Six step checklist for accountant local SEO covering setup research pages consistency reviews and tracking"
              className="inline-image"
            />
            <p className="body-copy">
              Steps like these take time to build and every firm starts from a
              different point. What stays consistent is the process. Set up the
              right foundation. Target the right searches. Keep everything
              consistent and accurate. That combination is what moves a firm
              from invisible to found.
            </p>
          </div>
        </section>

        <section className="servicePage-section faq-section">
          <div className="section-inner">
            <h2 className="section-title">Before You Book Your Call</h2>

            <details className="faq-item">
              <summary>How long until I see results?</summary>
              <p>
                Most accounting firms start seeing movement in local rankings
                within 60 to 90 days. Full results usually build over 4 to 6
                months since local SEO compounds over time.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I have to sign a long contract?</summary>
              <p>
                No. We work month to month. You stay because the results are
                working not because a contract locks you in.
              </p>
            </details>

            <details className="faq-item">
              <summary>What if I already have a website?</summary>
              <p>
                That is fine. We work with your current site and build the local
                SEO work on top of it. No rebuild required. We also work with
                website builders like Wix, Squarespace, and WordPress.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I need to understand SEO myself?</summary>
              <p>
                No. We handle the strategy, the setup, the research, and the
                monthly work. You get simple monthly reports that show exactly
                what changed and why.
              </p>
            </details>
          </div>
        </section>

        <section className="servicePage-section closer-section">
          <div className="section-inner closer-inner">
            <img
              src={phone}
              alt="Calendar with a call icon and the text free 20 minute call"
              className="inline-image closer-image"
            />

            <h2 className="section-title">
              Ready To Show Up First When Local Clients Search For An
              Accountant?
            </h2>
            <p className="body-copy">
              Book a free Competitor SEO breakdown and we will show you exactly
              where your firm ranks today. We will also look at your current
              local search visibility and let you know what might be missing.
            </p>
            <Link to="/contact" className="cta-button cta-button-large">
              Book A Free Call
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default LocalSEO;
